import { ROUTES } from '@/config/constants';
import env from '@/config/env';
import BetaBanner from '@/components/layout/BetaBanner';
import { Center, Loader, Text } from '@mantine/core';
import React from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

const EditorPage = React.lazy(() => import('@/features/app/PresentationEditor'));
const SavedPresentationsPage = React.lazy(() => import('@/features/app/PresentationsDashboard'));
const ExtractPage = React.lazy(() => import('@/features/app/Extract'));
const ProtectedRoute = React.lazy(() => import('@/components/auth/ProtectedRoute'));
const PremiumAccessGate = React.lazy(() => import('@/components/auth/PremiumAccessGate'));

const LandingPage = React.lazy(() => import('@/features/landing/Landing'));
const PricingPage = React.lazy(() => import('@/features/landing/Pricing'));
const HowItWorksPage = React.lazy(() => import('@/features/landing/HowItWorks'));
const AboutPage = React.lazy(() => import('@/features/landing/About'));
const ContactPage = React.lazy(() => import('@/features/marketing/Contact'));
const FAQPage = React.lazy(() => import('@/features/landing/FAQ'));
const TermsPage = React.lazy(() => import('@/features/legal/Terms'));
const PrivacyPage = React.lazy(() => import('@/features/legal/Privacy'));
const NotFoundPage = React.lazy(() => import('@/features/NotFound'));

const AppFallback = () => (
  <Center h="100vh">
    <Loader color="brand" />
    <Text c="dimmed" ml="sm">
      Loading...
    </Text>
  </Center>
);

const App: React.FC = () => {
  return (
    <BrowserRouter>
      {env.isBetaMode ? <BetaBanner /> : null}
      <React.Suspense fallback={<AppFallback />}>
        <Routes>
          <Route path={ROUTES.HOME} element={<LandingPage />} />
          <Route path={ROUTES.PRICING} element={<PricingPage />} />
          <Route path={ROUTES.HOW_IT_WORKS} element={<HowItWorksPage />} />
          <Route path={ROUTES.ABOUT} element={<AboutPage />} />
          <Route path={ROUTES.CONTACT} element={<ContactPage />} />
          <Route path={ROUTES.FAQ} element={<FAQPage />} />
          <Route path={ROUTES.TERMS} element={<TermsPage />} />
          <Route path={ROUTES.PRIVACY} element={<PrivacyPage />} />

          <Route
            path={ROUTES.EDITOR_WITH_ID}
            element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EDITOR}
            element={
              <ProtectedRoute>
                <EditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.SAVED_PRESENTATIONS}
            element={
              <ProtectedRoute>
                <PremiumAccessGate headerTitle="Dashboard">
                  <SavedPresentationsPage />
                </PremiumAccessGate>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.EXTRACT}
            element={
              <ProtectedRoute>
                <PremiumAccessGate headerTitle="Extract Text">
                  <ExtractPage />
                </PremiumAccessGate>
              </ProtectedRoute>
            }
          />

          <Route path={ROUTES.NOT_FOUND} element={<NotFoundPage />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
};

export default App;
